/**
 * Wrapper sécurisé pour localStorage et sessionStorage
 * Gère les cas où le stockage est indisponible (mode privé Safari, quotas dépassés, etc.)
 */

type StorageType = 'local' | 'session';

interface StorageWrapper {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => boolean;
  removeItem: (key: string) => boolean;
  clear: () => boolean;
  isAvailable: () => boolean;
}

// Cache pour éviter de tester la disponibilité à chaque appel
const availabilityCache: Record<StorageType, boolean | null> = {
  local: null,
  session: null,
};

// Fallback en mémoire quand le storage n'est pas disponible
const memoryStorage: Record<StorageType, Map<string, string>> = {
  local: new Map(),
  session: new Map(),
};

/**
 * Teste si un type de storage est disponible
 */
const testStorageAvailability = (type: StorageType): boolean => {
  if (typeof window === 'undefined') return false;
  
  // Retourner le cache si disponible
  if (availabilityCache[type] !== null) {
    return availabilityCache[type]!;
  }

  try {
    const storage = type === 'local' ? window.localStorage : window.sessionStorage;
    const testKey = `__storage_test_${Date.now()}__`;
    storage.setItem(testKey, 'test');
    storage.removeItem(testKey);
    availabilityCache[type] = true;
    return true;
  } catch (e) {
    // QuotaExceededError, SecurityError, ou autre
    availabilityCache[type] = false;
    if (import.meta.env.DEV) {
      console.warn(`[SafeStorage] ${type}Storage not available:`, e);
    }
    return false;
  }
};

/**
 * Crée un wrapper sécurisé pour un type de storage
 */
const createStorageWrapper = (type: StorageType): StorageWrapper => {
  const getStorage = () => 
    type === 'local' ? window.localStorage : window.sessionStorage;

  return {
    isAvailable: () => testStorageAvailability(type),

    getItem: (key: string): string | null => {
      try {
        if (testStorageAvailability(type)) {
          return getStorage().getItem(key);
        }
        return memoryStorage[type].get(key) ?? null;
      } catch (e) {
        if (import.meta.env.DEV) {
          console.warn(`[SafeStorage] Error reading ${key}:`, e);
        }
        return memoryStorage[type].get(key) ?? null;
      }
    },

    setItem: (key: string, value: string): boolean => {
      try {
        if (testStorageAvailability(type)) {
          getStorage().setItem(key, value);
          return true;
        }
        memoryStorage[type].set(key, value);
        return true;
      } catch (e) {
        // Quota dépassé ou autre erreur
        if (import.meta.env.DEV) {
          console.warn(`[SafeStorage] Error writing ${key}:`, e);
        }
        // Fallback en mémoire
        memoryStorage[type].set(key, value);
        return false;
      }
    },

    removeItem: (key: string): boolean => {
      try {
        if (testStorageAvailability(type)) {
          getStorage().removeItem(key);
        }
        memoryStorage[type].delete(key);
        return true;
      } catch (e) {
        if (import.meta.env.DEV) {
          console.warn(`[SafeStorage] Error removing ${key}:`, e);
        }
        memoryStorage[type].delete(key);
        return false;
      }
    },

    clear: (): boolean => {
      try {
        if (testStorageAvailability(type)) {
          getStorage().clear();
        }
        memoryStorage[type].clear();
        return true;
      } catch (e) {
        if (import.meta.env.DEV) {
          console.warn(`[SafeStorage] Error clearing storage:`, e);
        }
        memoryStorage[type].clear();
        return false;
      }
    },
  };
};

/**
 * localStorage sécurisé avec fallback en mémoire
 */
export const safeLocalStorage = createStorageWrapper('local');

/**
 * sessionStorage sécurisé avec fallback en mémoire
 */
export const safeSessionStorage = createStorageWrapper('session');

/**
 * Helpers pour stocker/récupérer des objets JSON
 */
export const safeStorageJSON = {
  get: <T>(key: string, storage: StorageWrapper = safeLocalStorage): T | null => {
    try {
      const value = storage.getItem(key);
      return value ? JSON.parse(value) as T : null;
    } catch (e) {
      if (import.meta.env.DEV) {
        console.warn(`[SafeStorage] Error parsing JSON for ${key}:`, e);
      }
      return null;
    }
  },

  set: <T>(key: string, value: T, storage: StorageWrapper = safeLocalStorage): boolean => {
    try {
      return storage.setItem(key, JSON.stringify(value));
    } catch (e) {
      if (import.meta.env.DEV) {
        console.warn(`[SafeStorage] Error stringifying JSON for ${key}:`, e);
      }
      return false;
    }
  },
};

/**
 * Hook-like helper pour utiliser le storage avec une valeur par défaut
 */
export const getStorageValue = <T>(
  key: string, 
  defaultValue: T, 
  storage: StorageWrapper = safeLocalStorage
): T => {
  const stored = safeStorageJSON.get<T>(key, storage);
  return stored !== null ? stored : defaultValue;
};

/**
 * Vérifie si le storage est disponible (pour afficher des avertissements à l'utilisateur si besoin)
 */
export const isStorageAvailable = (type: StorageType = 'local'): boolean => {
  return testStorageAvailability(type);
};
